/**
 * This file includes polyfills needed by Angular and is loaded before the app.
 * You can add your own extra polyfills to this file.
 *
 * This file is divided into 2 sections:
 *   1. Browser polyfills. These are applied before loading ZoneJS and are sorted by browsers.
 *   2. Application imports. Files imported after ZoneJS that should be loaded before your main
 *      file.
 *
 * The current setup is for so-called "evergreen" browsers; the last versions of browsers that
 * automatically update themselves. This includes Safari >= 10, Chrome >= 55 (including Opera),
 * Edge >= 13 on the desktop, and iOS 10 and Chrome on mobile.
 *
 * Learn more in https://angular.io/guide/browser-support
 */
/***************************************************************************************************
 * BROWSER POLYFILLS
 */
/**
 * By default, zone.js will patch all possible macroTask and DomEvents
 * user can disable parts of macroTask/DomEvents patch by setting following flags
 * because those flags need to be set before `zone.js` being loaded, and webpack
 * will put import in the top of bundle, so user need to create a separate file
 * in this directory (for example: zone-flags.ts), and put the following flags
 * into that file, and then add the following code before importing zone.js.
 * import './zone-flags';
 *
 * The flags allowed in zone-flags.ts are listed here.
 *
 * The following flags will work for all browsers.
 *
 * (window as any).__Zone_disable_requestAnimationFrame = true; // disable patch requestAnimationFrame
 * (window as any).__Zone_disable_on_property = true; // disable patch onProperty such as onclick
 * (window as any).__zone_symbol__UNPATCHED_EVENTS = ['scroll', 'mousemove']; // disable patch specified eventNames
 *
 *  in IE/Edge developer tools, the addEventListener will also be wrapped by zone.js
 *  with the following flag, it will bypass `zone.js` patch for IE/Edge
 *
 *  (window as any).__Zone_enable_cross_context_check = true;
 *
 */

// Global HTTP error interceptor for both fetch and XHR
((): void => {
  function sanitizeLogValue(value: string): string {
    return value
      .replace(/[\r\n]/g, '') // prevent multi-line log forging
      .replace(/[^\x20-\x7E]/g, ''); // remove control characters
  }

  // ——— 1) PATCH fetch ———
  const originalFetch = window.fetch.bind(window);

  window.fetch =
    (async (input, init?): Promise<Response> => {
      const res = await originalFetch(input, init);
      if (!res.ok) {
        const baseUrl = sanitizeLogValue(new URL(res.url).origin + new URL(res.url).pathname);
        const statusText = sanitizeLogValue(res.statusText);
        console.error(
          '[Fetch Error] HTTP request failed\n' +
          `  URL   : ${baseUrl}\n` +
          `  Status: ${res.status} ${statusText}`,
        );
      }

      return res;
    });

  // ——— 2) PATCH XMLHttpRequest ———
  const NativeXHR = window.XMLHttpRequest;
  window.XMLHttpRequest = new Proxy(NativeXHR, {
    construct(Target, args): object {
      const xhr = Reflect.construct(Target, args);

      xhr.addEventListener('loadend', () => {
        if (xhr.status >= 400) {
          const { responseURL, status, statusText } = xhr;
          const baseUrl = responseURL ? sanitizeLogValue(new URL(responseURL).origin + new URL(responseURL).pathname) : '[UNKNOWN]';
          const sanitizedStatusText = sanitizeLogValue(statusText);
          console.error(
            '[XHR Error] HTTP request failed\n' +
            `  URL   : ${baseUrl}\n` +
            `  Status: ${status} ${sanitizedStatusText}`,
          );
        }
      });

      return xhr;
    },
  });
})();

/***************************************************************************************************
 * Zone JS is required by default for Angular itself.
 */
import 'zone.js';
import 'core-js/actual/array';
import 'core-js/actual/object';
import 'core-js/actual/symbol';
import 'core-js/actual/function';
import 'core-js/actual/parse-int';
import 'core-js/actual/parse-float';
import 'core-js/actual/number';
import 'core-js/actual/math';
import 'core-js/actual/string';
import 'core-js/actual/date';
import 'core-js/actual/regexp';
import 'core-js/actual/map';
import 'core-js/actual/set';
import 'core-js/actual/reflect';

const win = (window as unknown as Record<string, unknown>);
win.__Zone_disable_requestAnimationFrame = true;
win.__Zone_disable_on_property = true;
win.__zone_symbol__UNPATCHED_EVENTS = ['scroll', 'mousemove'];
win.__Zone_enable_cross_context_check = true;
// Working around an intermittent issue
if (!win.global) {
  win.global = window;
}

